import React, { memo, useContext, useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch } from 'react-redux';
import { ModalContext } from 'src/contexts/modal-context';
import { editableFields } from '../constants';
import { FormBuilder } from '@components/form';
import { translate } from '@components/i18n';
import {
    createItemDeadline,
    removeItemDeadline,
    updateItemDeadline,
} from '@redux/slices/item-deadline';
import { ItemDeadlineResponse } from '@models/item-deadline';

interface EditModalProps {
    data?: ItemDeadlineResponse;
}

export const EditModal: React.FC<EditModalProps> = memo(({ data }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<ItemDeadlineResponse>();
    const [editState, setEditState] = useState<ItemDeadlineResponse>(data);
    const modalContext = useContext(ModalContext);

    const onOk = (values): void => {
        if (!data) addNew(values);
        else handleUpdate(values);
    };

    const handleUpdate = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.updatingItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            updateItemDeadline({
                data: {
                    id: data.id,
                    ...values,
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                },
            }),
        );
    };

    const addNew = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.registeringItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            createItemDeadline({
                data: values,
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                },
            }),
        );
    };

    useEffect(() => {
        if (data) {
            setEditState(data);
            form.setFieldsValue(data);
        } else {
            form.resetFields();
        }
    }, [data]);

    const onChangeValues = (
        changedValues: Partial<ItemDeadlineResponse>,
        allValues: ItemDeadlineResponse,
    ): void => {
        setEditState({
            ...allValues,
            ...changedValues,
        });
    };

    const handleDeleteUser = (): void => {
        dispatch(removeItemDeadline(data.id));
        modalContext.handleClose();
    };

    return (
        <Row align="middle">
            <Col span={1} />
            <Col span={22}>
                <FormBuilder
                    onValuesChange={onChangeValues}
                    layout="vertical"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{ ...editState }}
                    onFinish={onOk}
                    form={form}
                    fields={editableFields()}
                    withDelete={!!data}
                    onDelete={handleDeleteUser}
                />
            </Col>
            <Col span={1} />
        </Row>
    );
});
